<template>
  <bean-source-page
    :type="type"
    :columns="columns"
    :form-columns="formColumns"
    :filter-columns="filterColumns"
    :actions="actions"
    :form="form"
    :title="$t('model.adjustment')"
    resource="adjustments"
    :actionColumnProps="{ width: 200 }"
    :batch-actions="batchActions"
  />
</template>

<script>
import { Vue, Component, Prop, Mixins } from 'vue-property-decorator';
import { filterColumnsExtend, columnsExtend, formColumnsExtend } from '../admin_grapes/adjustments.js';
import { displayText } from '@/custom_models_extend';
import DialogText from '@/components/dialog-text.vue';
import { abilityService } from 'beans-admin-plugin';
import _ from 'lodash';
import Utils from '../components/utils.vue';

@Component
export default class Adjustments extends Mixins(Vue, Utils) {
  @Prop(String) type;

  // form 新建表单初始化数据（default数据）
  form = {}

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  filterColumns() {
    const array = [
      {
        prop: 'adjustable_of_SalesOrder_type_id_eq',
        label: '销售订单',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            filterable: true,
            placeholder: '销售订单',
            xRemotePreload: async () => {
              const { data } = await this.$request.get('/sales_orders/read_options', { per_page: 100 });
              return data.map((object) => ({ value: object.id, label: displayText(object) }));
            },
            xRemoteSearch: async (keyword) => {
              const { data } = await this.$request.get('/sales_orders/read_options', { keyword });
              return data.map((object) => ({ value: object.id, label: displayText(object) }));
            }
          },
          domProps: {
            style: 'width: 100%'
          }
        }
      },
      {
        prop: 'adjustable_of_JushuitanOrder_type_o_id_eq',
        label: '聚水潭订单',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            filterable: true,
            placeholder: '聚水潭订单',
            xRemotePreload: async () => {
              const { data } = await this.$request.get('/jushuitan_orders/read_options', { per_page: 100 });
              return data.map((object) => ({ value: object.id, label: displayText(object) }));
            },
            xRemoteSearch: async (keyword) => {
              const { data } = await this.$request.get('/jushuitan_orders/read_options', { keyword });
              return data.map((object) => ({ value: object.id, label: displayText(object) }));
            }
          },
          domProps: {
            style: 'width: 100%'
          }
        }
      },
      abilityService.can('users.read_options') && {
        key: 'creator_id_eq',
        renderCell: {
          props: {
            clearable: true,
            xRemotePreload: async () => {
              return (await this.$request.get('users/read_options', { per_page: 100, ...this.userOptionsFilter })).data.map(item => ({ value: item.id, label: displayText(item) }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('users/read_options', { keyword, ...this.userOptionsFilter })).data.map(item => ({ value: item.id, label: displayText(item) }));
            }
          },
        }
      }
    ].filter(Boolean)

    return filterColumnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  columns(h, { type }) {
    const array = [
      type === 'show' && 'id',
      {
        key: 'adjustable',
        width: 360,
      },
      {
        key: 'amount',
        width: 120,
      },
      {
        key: 'creator',
        width: 120,
      },
      {
        key: 'remark',
        minWidth: 200,
        renderCell: (h, { props: { value } }) => {
          return <DialogText
            title="备注"
            value={value}
            maxRow={3}
            showAll={type === 'show'}
          />
        }
      },
      type === 'show' && 'created_at',
      type === 'show' && 'updated_at',
    ].filter(Boolean)

    return columnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource - RESTful api resoure name
   * @param {Object} params.data - current form data
   * @param {Function} params.change - invoke this method to sync form change({ field: 'new value' })
   */
  formColumns(h, { data, type, change }) {
    const array = [
      {
        prop: 'adjustable_type',
        label: '对象类型',
        defaultValue: 'SalesOrder',
        renderCell: {
          component: 'select',
          props: {
            clearable: false,
            options: [
              { label: '销售订单', value: 'SalesOrder' },
              { label: '聚水潭订单', value: 'JushuitanOrder' }
            ],
          },
          on: {
            change: (value) => {
              // 切换类型时清空关联对象
              if (value !== data.adjustable_type && data.adjustable_id) {
                change({ adjustable_id: null });
              }
            }
          },
          domProps: {
            style: 'width: 100%'
          }
        }
      },
      {
        prop: 'adjustable_id',
        label: data.adjustable_type === 'JushuitanOrder' ? '聚水潭订单' : '销售订单',
        renderCell: {
          component: 'app-read-options',
          required: true,
          key: data.adjustable_type || 'SalesOrder',
          props: {
            clearable: true,
            url: '/adjustments/adjustables',
            params: {
              record_type_eq: data.adjustable_type || 'SalesOrder',
            },
            labelFormat: displayText,
            value: data.adjustable_id,
            onInitOptions: async () => {
              if (data.adjustable && data.adjustable.id === data.adjustable_id) {
                return [data.adjustable];
              }
              return [];
            },
          },
          on: {
            select: (value) => {
              change({ adjustable_id: value ? value.record_id : null });
              if (value && type === 'new') {
                const amount = value.cn === 'JushuitanOrder' ? Number(value.amount) : Number(value.sales_amount);
                if (amount && !data.amount) {
                  change({ amount });
                }
              }
            }
          },
          domProps: {
            style: 'width: 100%'
          }
        }
      },
      'amount',
      'remark',
    ].filter(Boolean)

    return formColumnsExtend(array, { data: data, type: type })
  }

  /**
   * @param {Object} scope -页面/表格 scope 数据
   * @param {Object} scope.row - 详情页/表格行 数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  actions({ row }, { remove }) {
    return [
      row.can_update && {
        component: 'routeButton',
        props: {
          label: this.$t('edit'),
          to: { name: 'adjustments.edit', params: { id: row.id } }
        }
      },
      row.can_destroy && {
        component: 'confirmButton',
        props: {
          label: this.$t('destroy'),
          type: 'danger',
          can: 'adjustments.destroy'
        },
        on: {
          confirm: () => remove(row.id)
        }
      }
    ].filter(Boolean);
  }

  batchActions({ selection }, { resource }) {
    const exportClick = async (selection) => {
      if (selection.length === 0) {
        await this.$autoLoading(this.$request.get(`/${resource}/export`, { ...this.$route.query }));
      } else {
        await this.$autoLoading(this.$request.get(`/${resource}/export`, { ...this.$route.query, id_in: _.map(selection, item => item.id) }));
      }

      this.$message.success(this.$t('exporting'));
    }

    return [
      () => <el-button type='primary' onClick={() => exportClick(selection)}><i class='el-icon-download'></i>{this.$t('export')}</el-button>,
    ]
  }
}

export const route = [
  {
    path: '/adjustments',
    name: 'adjustments.index',
    component: Adjustments,
    props: {
      type: 'index',
    }
  },
  {
    path: '/adjustments/new',
    name: 'adjustments.new',
    component: Adjustments,
    props: {
      type: 'new',
    }
  },
  {
    path: '/adjustments/:id',
    name: 'adjustments.show',
    component: Adjustments,
    props: {
      type: 'show',
    }
  },
  {
    path: '/adjustments/:id/edit',
    name: 'adjustments.edit',
    component: Adjustments,
    props: {
      type: 'edit'
    }
  }
]
</script>
